import { useEffect, useId, useRef, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ErroApi, type Usuario } from '../api/cliente';
import { dataLegal, VERSAO_PRIVACIDADE, VERSAO_TERMOS } from '../../shared/legal';
import { AvisoErro, Botao } from './UI';

/**
 * NOVO ACEITE DE TERMOS E PRIVACIDADE.
 *
 * Quando os textos mudam de conteúdo, quem já tem conta precisa concordar com
 * a versão nova antes de seguir usando o app. É um `<dialog>` nativo que NÃO
 * fecha no Esc: fechar sem responder deixaria a pessoa usando o app sob um
 * texto que ela não aceitou. As duas saídas são aceitar ou sair da conta.
 *
 * Some nas páginas dos próprios Termos e Privacidade, para dar para ler com
 * calma antes de decidir. A equipe no "acessar como" não vê o pedido: não pode
 * aceitar por ninguém.
 */
const PAGINAS_LEGAIS = ['/termos', '/privacidade'];

export function ModalReaceite({ usuario }: { usuario: Usuario }) {
  const caminho = useRouterState({ select: (s) => s.location.pathname });
  const aberto = !!usuario.aceite_pendente && !usuario.acesso_admin && !PAGINAS_LEGAIS.includes(caminho);
  const ref = useRef<HTMLDialogElement>(null);
  const idTitulo = useId();
  const idCaixa = useId();
  const [li, setLi] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const qc = useQueryClient();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (aberto && !d.open) d.showModal?.();
    if (!aberto && d.open) d.close?.();
  }, [aberto]);

  const aceitar = useMutation({
    mutationFn: () => api.post<{ ok: true }>('/eu/aceite-legal', {
      versao_termos: VERSAO_TERMOS, versao_privacidade: VERSAO_PRIVACIDADE,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['eu'] }),
    onError: (e) => setErro(e instanceof ErroApi ? e.message : 'Não conseguimos registrar agora. Tente de novo.'),
  });
  const sair = useMutation({
    mutationFn: () => api.post<{ ok: true }>('/auth/sair'),
    onSettled: () => { qc.clear(); window.location.assign('/'); },
  });

  if (!usuario.aceite_pendente) return null;

  return (
    <dialog
      ref={ref}
      aria-labelledby={idTitulo}
      className="popup-novidades"
      onCancel={(e) => e.preventDefault()}
    >
      <div className="flex flex-col gap-4">
        <h2 id={idTitulo} className="text-2xl font-bold">Atualizamos nossos termos</h2>
        <p>
          Os Termos de Uso e a Política de Privacidade mudaram em {dataLegal(VERSAO_TERMOS)}.
          Para continuar usando o Chama Um, leia e diga se concorda.
        </p>
        <p>O que mudou, em resumo:</p>
        <ul className="flex list-disc flex-col gap-2 pl-6">
          <li>Perfis de profissionais aprovados podem aparecer no Google e em outros buscadores.</li>
          <li>Você pode juntar fotos à sua avaliação; a equipe olha antes de publicar.</li>
          <li>Novo quadro de sugestões para você ajudar a melhorar o app.</li>
          <li>Regras para devolução de Pix pedida pelo banco e para pedidos de emergência.</li>
          <li>Ao excluir a conta, também pedimos às empresas de pagamento que apaguem seu cadastro.</li>
        </ul>
        <p>
          Leia: <Link to="/termos" className="acao-link">Termos de Uso</Link> e{' '}
          <Link to="/privacidade" className="acao-link">Política de Privacidade</Link>.
        </p>

        <div className="flex items-start gap-3">
          <input
            id={idCaixa} type="checkbox" className="mt-1 h-6 w-6"
            checked={li} onChange={(e) => { setLi(e.target.checked); setErro(null); }}
          />
          <label htmlFor={idCaixa}>Li e concordo com os Termos de Uso e a Política de Privacidade.</label>
        </div>

        {erro && <AvisoErro>{erro}</AvisoErro>}

        <div className="flex flex-wrap items-center gap-4">
          <Botao type="button" onClick={() => aceitar.mutate()} disabled={!li} carregando={aceitar.isPending}>
            Aceitar e continuar
          </Botao>
          <Botao type="button" variante="secundario" onClick={() => sair.mutate()} carregando={sair.isPending}>
            Não concordo, sair da conta
          </Botao>
        </div>
        <p className="text-[var(--color-text-muted)]">
          Se não concordar, você pode sair agora e pedir a exclusão da conta pelo suporte.
        </p>
      </div>
    </dialog>
  );
}
